import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import classes from './addRowModalLVTime.module.scss';

interface FormData {
  roomTypeId: number;
  incomingJobTypeId: number;
  bottleCountMin: number;
  bottleCountMax: number;
  dosageForm: string;
  lvTimeMinutes: number;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface AddRowModalLvTimeProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialValues?: FormData | null;
  isEdit: boolean;
  roomOptions: DropdownOption[];
  incomingJobTypeOptions: DropdownOption[];
  dosageFormOptions: DropdownOption[];
}

const AddRowModalLvTime = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit,
  roomOptions,
  incomingJobTypeOptions,
  dosageFormOptions
}: AddRowModalLvTimeProps) => {
  const [formData, setFormData] = useState<FormData>({
    roomTypeId: 0,
    incomingJobTypeId: 0,
    bottleCountMin: 0,
    bottleCountMax: 0,
    dosageForm: '',
    lvTimeMinutes: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const modalSessionRef = useRef<number>(0);
  const initializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (visible) {
      // Track modal session - increment when modal opens
      if (!initializedRef.current) {
        modalSessionRef.current += 1;
        initializedRef.current = true;

        // Only update form when modal first opens
        if (initialValues) {
          setFormData({ ...initialValues });
        } else {
          // Reset form for new entry
          setFormData({
            roomTypeId: 0,
            incomingJobTypeId: 0,
            bottleCountMin: 0,
            bottleCountMax: 0,
            dosageForm: '',
            lvTimeMinutes: 0
          });
        }
        setErrors({});
      }
    } else {
      // Modal closed, reset initialization flag
      initializedRef.current = false;
    }
    // Note: initialValues is intentionally not in dependencies to prevent re-initialization
    // when parent component re-renders after store updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.roomTypeId || formData.roomTypeId === 0) {
      newErrors.roomTypeId = 'Room Type is required';
    }
    if (!formData.incomingJobTypeId || formData.incomingJobTypeId === 0) {
      newErrors.incomingJobTypeId = 'Run Type is required';
    }
    if (formData.bottleCountMin === null || formData.bottleCountMin === undefined) {
      newErrors.bottleCountMin = 'Bottle Count Min is required';
    }
    if (formData.bottleCountMax === null || formData.bottleCountMax === undefined) {
      newErrors.bottleCountMax = 'Bottle Count Max is required';
    }
    if (!formData.dosageForm) {
      newErrors.dosageForm = 'Dosage Form is required';
    }

    // LV Time validation
    if (formData.lvTimeMinutes === null || formData.lvTimeMinutes === undefined) {
      newErrors.lvTimeMinutes = 'LV Time is required';
    } else if (formData.lvTimeMinutes < 0) {
      newErrors.lvTimeMinutes = 'LV Time cannot be negative';
    } else if (formData.lvTimeMinutes > 720) {
      newErrors.lvTimeMinutes = 'LV Time cannot exceed 720 minutes (12 hours)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
        // Close modal after store updates complete
        setTimeout(() => {
          onHide();
        }, 100);
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for LV Time!');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Dialog
      header={isEdit ? 'Edit LV Time Configuration' : 'Add New LV Time Configuration'}
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '500px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formGroup}>
          <label>Room Type</label>
          <Dropdown
            value={formData.roomTypeId}
            options={roomOptions}
            onChange={e => handleInputChange('roomTypeId', e.value)}
            placeholder="Select Room Type"
            className={`${classes.dropdown} ${errors.roomTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.roomTypeId && <small className="p-error">{errors.roomTypeId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Incoming Job Type</label>
          <Dropdown
            value={formData.incomingJobTypeId}
            options={incomingJobTypeOptions}
            onChange={e => handleInputChange('incomingJobTypeId', e.value)}
            placeholder="Select Run Type"
            className={`${classes.dropdown} ${errors.incomingJobTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.incomingJobTypeId && (
            <small className="p-error">{errors.incomingJobTypeId}</small>
          )}
        </div>

        <div className={classes.formGroup}>
          <label>Bottle Count Min</label>
          <InputText
            value={formData.bottleCountMin.toString()}
            onChange={e => handleInputChange('bottleCountMin', parseInt(e.target.value || '0', 10))}
            keyfilter="pint"
            placeholder="Enter Bottle Count Min"
            className={`${classes.inputText} ${errors.bottleCountMin ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.bottleCountMin && <small className="p-error">{errors.bottleCountMin}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Bottle Count Max</label>
          <InputText
            value={formData.bottleCountMax.toString()}
            onChange={e => handleInputChange('bottleCountMax', parseInt(e.target.value || '0', 10))}
            keyfilter="pint"
            placeholder="Enter Bottle Count Max"
            className={`${classes.inputText} ${errors.bottleCountMax ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.bottleCountMax && <small className="p-error">{errors.bottleCountMax}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Dosage Form</label>
          <Dropdown
            value={formData.dosageForm}
            options={dosageFormOptions}
            onChange={e => handleInputChange('dosageForm', e.value)}
            placeholder="Select Dosage Form"
            className={`${classes.dropdown} ${errors.dosageForm ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.dosageForm && <small className="p-error">{errors.dosageForm}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>LV Time (minutes)</label>
          <InputText
            value={formData.lvTimeMinutes.toString()}
            onChange={e => handleInputChange('lvTimeMinutes', parseInt(e.target.value || '0', 10))}
            keyfilter="pint"
            placeholder="Enter LV time in minutes"
            className={`${classes.inputText} ${errors.lvTimeMinutes ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.lvTimeMinutes && <small className="p-error">{errors.lvTimeMinutes}</small>}
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />
        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalLvTime;
